import { useContext } from 'react'
import { createBrowserRouter, Navigate, Outlet, RouterProvider, useLocation } from 'react-router'
import { FullStory } from '@fullstory/browser'
import { LicenseInfo } from '@mui/x-license'

import PublicRouteWrapper from './components/public-route-wrapper'
import RouteWrapper from './components/route-wrapper'
import ActivationProvider from './providers/activation-provider'
import { AuthenticationContext } from './providers/auth-provider'
import DashboardProvider from './providers/dashboard-provider'
import NavProvider from './providers/nav-provider'
import AlertAcknowledge from './routes/alert-acknowledge'
import AlertSubmit from './routes/alert-submit'
import Alerts from './routes/alerts'
import AmazonSidewalk from './routes/amazon-sidewalk'
import CompleteSetup from './routes/complete-setup'
import ConfirmLocation from './routes/confirm-location'
import Contacts from './routes/contacts'
import Contact from './routes/contacts/contact'
import Dashboard from './routes/dashboard'
import DeviceDetails from './routes/dashboard/device-details'
import DeviceConnection from './routes/device-connection'
import DeviceRoster from './routes/device-roster'
import Help from './routes/help'
import HigherFloor from './routes/higher-floor'
import LeakPins from './routes/leak-pins'
import Login from './routes/login'
import Logout from './routes/logout'
import MeetSensor from './routes/meet-sensor'
import OrderHistory from './routes/order-history'
import OrderDetails from './routes/order-history/order-details'
import ParticipationAgreement from './routes/participation-agreement'
import ParticipationAgreementMultiple from './routes/participation-agreement-multiple'
import EnterPasscode from './routes/passcode'
import PlacementSuggestions from './routes/placement-suggestions'
import PlacementTips from './routes/placement-tips'
import SensorActivation from './routes/sensor-activation'
import SensorFolderAssignment from './routes/sensor-folder-assignment'
import SensorLocation from './routes/sensor-location'
import TurnOnSensor from './routes/turn-on-sensor'
import WhereToAvoid from './routes/where-to-avoid'
import { LOGOUT_TOKEN, MESHIFYID_TOKEN, SIDEWALKID_TOKEN } from './utils/constants'
import { fixQueryString, saveDeviceIds } from './utils/helpers'

const PrivateLoginRouteWrapper = () => {
  const location = useLocation()
  const queryString = fixQueryString(location.pathname)
  const { alertAckData, alertAckResponse, alertAckCode, deviceId } = location.state || {}
  const meshifyId = localStorage.getItem(MESHIFYID_TOKEN)
  const sidewalkId = localStorage.getItem(SIDEWALKID_TOKEN)

  if (alertAckData && alertAckResponse && alertAckCode && deviceId)
    return (
      <Navigate
        to={`/submit?type=${alertAckResponse}`}
        state={{
          data: alertAckData,
          code: alertAckCode,
          deviceId,
          fromLogin: true,
        }}
        replace
      />
    )

  if (meshifyId && sidewalkId) return <Navigate to={`/sensor-activation${queryString}`} replace />
  if (location.state?.from?.pathname) return <Navigate to={`${location.state.from.pathname}${location.state.from.search || ''}`} replace />
  return <Navigate to="/dashboard" replace />
}

const PrivateRouteWrapper = () => {
  const location = useLocation()
  const { isAuthenticated } = useContext(AuthenticationContext)
  const loggedOut = localStorage.getItem(LOGOUT_TOKEN) === 'true'

  if (!isAuthenticated) return <Navigate to="/login" replace state={loggedOut ? {} : { from: location }} />
  return <Outlet />
}

const PublicRoute404 = () => {
  const location = useLocation()
  const queryString = fixQueryString(location.pathname)
  const loggedOut = localStorage.getItem(LOGOUT_TOKEN) === 'true'
  return <Navigate to={`/login${queryString}`} replace state={loggedOut ? {} : { from: location }} />
}

export const authenticatedRoutes = [
  {
    element: <PrivateLoginRouteWrapper />,
    children: [
      {
        path: '/login',
      },
      {
        path: '/',
      },
      {
        path: '*',
      },
    ],
  },
  {
    element: <PrivateRouteWrapper />,
    children: [
      // Dashboard Routes
      {
        element: (
          <DashboardProvider>
            <NavProvider>
              <RouteWrapper />
            </NavProvider>
          </DashboardProvider>
        ),
        children: [
          {
            path: '/dashboard',
            element: <Dashboard />,
          },
          {
            path: '/dashboard/:folderId',
            element: <Dashboard />,
          },
          {
            path: '/dashboard/:folderId/devices',
            element: <Dashboard />,
            children: [
              {
                path: ':deviceId',
                element: <DeviceDetails />,
              },
            ],
          },
          {
            path: '/dashboard/:folderId/contacts',
            element: <Contacts />,
            children: [
              {
                path: ':userId',
                element: <Contact />,
              },
            ],
          },
          {
            path: '/dashboard/:folderId/alerts',
            element: <Alerts />,
            children: [
              {
                path: 'device/:deviceId',
                element: <DeviceDetails />,
              },
            ],
          },
          {
            path: '/dashboard/:folderId/order-history',
            element: <OrderHistory />,
            children: [
              {
                path: ':orderNumber',
                element: <OrderDetails />,
              },
            ],
          },
          {
            path: '/dashboard/:folderId/help',
            element: <Help />,
          },
        ],
      },
      {
        // Activation Routes
        element: (
          <ActivationProvider>
            <Outlet />
          </ActivationProvider>
        ),
        children: [
          // Activation Routes with Dark Background
          {
            element: <RouteWrapper useDarkBackground={true} />,
            children: [
              {
                path: '/meet-sensor',
                element: <MeetSensor />,
              },
              {
                path: '/amazon-sidewalk',
                element: <AmazonSidewalk />,
              },
              {
                path: '/higher-floor',
                element: <HigherFloor />,
              },
              {
                path: '/placement-tips',
                element: <PlacementTips />,
              },
              {
                path: '/where-to-avoid',
                element: <WhereToAvoid />,
              },
              {
                path: '/turn-on-sensor',
                element: <TurnOnSensor />,
              },
              {
                path: '/device-connection',
                element: <DeviceConnection />,
              },
              {
                path: '/leak-pins',
                element: <LeakPins />,
              },
              {
                path: '/placement-suggestions',
                element: <PlacementSuggestions />,
              },
              {
                path: '/sensor-location',
                element: <SensorLocation />,
              },
            ],
          },
          {
            // Activation Routes with Light Background
            element: <RouteWrapper />,
            children: [
              {
                path: '/device-roster/:folderId',
                element: <DeviceRoster />,
              },
              {
                path: '/sensor-activation',
                element: <SensorActivation />,
              },
              {
                path: '/sensor-folder-assignment',
                element: <SensorFolderAssignment />,
              },
              {
                path: '/confirm-location',
                element: <ConfirmLocation />,
              },
              {
                path: '/complete-setup',
                element: <CompleteSetup />,
              },
            ],
          },
        ],
      },
      // Alert Acknowledgement Routes
      {
        element: <PublicRouteWrapper variant="alert-acknowledgement" routeType="authenticated" />,
        children: [
          {
            path: '/acknowledge',
            element: <AlertAcknowledge />,
          },
          {
            path: '/submit',
            element: <AlertSubmit />,
          },
        ],
      },
      // All other Private routes
      {
        element: <RouteWrapper />,
        children: [
          {
            path: '/participation-agreement',
            element: <ParticipationAgreement />,
          },
          {
            path: '/participation-agreement-multiple',
            element: <ParticipationAgreementMultiple />,
          },
          {
            path: '/onboarding-contacts',
            element: <Contacts />,
            children: [
              {
                path: ':userId',
                element: <Contact />,
              },
            ],
          },
          {
            path: '/logout',
            element: <Logout />,
          },
        ],
      },
    ],
  },
]

export const unauthenticatedRoutes = [
  {
    element: <PublicRoute404 />,
    children: [
      {
        path: '/',
      },
      {
        path: '/sensor-activation',
      },
      {
        path: '/sensor-folder-assignment',
      },
      {
        path: '/meet-sensor',
      },
      {
        path: '/complete-setup',
      },
      {
        path: '/sensor-location',
      },
      {
        path: '/dashboard',
      },
      {
        path: '/contacts',
      },
      {
        path: '/alerts',
      },
      {
        path: '/placement-suggestions',
      },
      {
        path: '/logout',
      },
      {
        path: '/turn-on-sensor',
      },
      {
        path: '/device-connection',
      },
      {
        path: '/leak-pins',
      },
      {
        path: '/placement-tips',
      },
      {
        path: '*',
      },
    ],
  },
  {
    element: <PublicRouteWrapper variant="default" routeType="unauthenticated" />,
    children: [
      {
        path: '/login',
        element: <Login />,
      },
      {
        path: '/passcode',
        element: <EnterPasscode />,
      },
    ],
  },
  {
    element: <PublicRouteWrapper variant="alert-acknowledgement" routeType="unauthenticated" />,
    children: [
      {
        path: '/acknowledge',
        element: <AlertAcknowledge />,
      },
      {
        path: '/submit',
        element: <AlertSubmit />,
      },
    ],
  },
]

const MUI_LICENSE_KEY = import.meta.env.VITE_MUI_LICENSE_KEY
LicenseInfo.setLicenseKey(MUI_LICENSE_KEY)

const App = () => {
  const { isAuthenticated, ownUser } = useContext(AuthenticationContext)
  saveDeviceIds()

  FullStory('setIdentity', {
    uid: String(ownUser?.id),
    properties: {
      userId: ownUser?.id,
    },
  })

  // Use a single router instance that conditionally renders routes based on authentication
  // This prevents router switching issues that cause white pages and state leakage
  const router = createBrowserRouter(isAuthenticated ? authenticatedRoutes : unauthenticatedRoutes)

  return <RouterProvider router={router} key={isAuthenticated ? 'authenticated' : 'unauthenticated'} />
}

export default App
